/**
 * TimerOverlay – fullskärms-nedräkningstimer för workshops och SWOT.
 *
 * Props:
 *   open         boolean           – om overlayn visas
 *   onClose      () => void        – stäng / avbryt
 *   label        string            – vad som räknas ner, t.ex. kvadrantnamn / fråga
 *   color        string            – accentfärg för ruta/cirkeln
 *   Icon         MUI-ikon-komponent (valfritt)
 *   totalSeconds number            – total tid i sekunder
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography, IconButton, Dialog } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';

function pad(n) { return String(n).padStart(2, '0'); }

function formatTime(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${pad(m)}:${pad(s)}`;
}

// ── Web Audio helpers ──────────────────────────────────────────────────────────
function getAudioCtx() {
  if (!window._timerAudioCtx) {
    window._timerAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return window._timerAudioCtx;
}

function playTick() {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.18, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.07);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.08);
  } catch {}
}

function playAlarm() {
  try {
    const ctx = getAudioCtx();
    const freqs = [880, 1100, 880, 1100, 880];
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = f;
      osc.type = 'square';
      const t = ctx.currentTime + i * 0.22;
      gain.gain.setValueAtTime(0.12, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
      osc.start(t);
      osc.stop(t + 0.2);
    });
  } catch {}
}

export default function TimerOverlay({ open, onClose, label, color = '#E05500', Icon, totalSeconds }) {
  const [remaining, setRemaining] = useState(totalSeconds);
  const [running,   setRunning]   = useState(true); // börjar direkt
  const intervalRef = useRef(null);

  // Återställ när den öppnas med ny tid
  useEffect(() => {
    if (open) {
      setRemaining(totalSeconds);
      setRunning(true);
    }
  }, [open, totalSeconds]);

  // Tick
  useEffect(() => {
    if (!open) return;
    if (running && remaining > 0) {
      intervalRef.current = setInterval(() => {
        setRemaining(r => {
          const next = r - 1;
          if (next <= 0) {
            clearInterval(intervalRef.current);
            setRunning(false);
            playAlarm();
            return 0;
          }
          // Tickande ljud de sista 10 sekunderna
          if (next <= 10) playTick();
          return next;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [open, running, remaining]);

  const toggle = useCallback(() => setRunning(r => !r), []);
  const stop   = useCallback(() => { setRunning(false); onClose(); }, [onClose]);

  const fraction = totalSeconds > 0 ? remaining / totalSeconds : 0;
  const done = remaining === 0;

  // Färg: orange vid ≤2min, röd vid ≤1min
  const ringColor = done || remaining <= 60
    ? '#ef4444'
    : remaining <= 120
      ? '#FF9800'
      : color;

  // SVG progress ring
  const R = 140;
  const CIRC = 2 * Math.PI * R;
  const dash = CIRC * fraction;

  return (
    <Dialog
      open={open}
      onClose={stop}
      fullScreen
      PaperProps={{ sx: { bgcolor: 'transparent' } }}
      BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(6px)' } }}
    >
      <Box sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 5,
        position: 'relative',
        userSelect: 'none',
      }}>

        {/* Stäng-knapp överst till höger */}
        <IconButton
          onClick={stop}
          sx={{
            position: 'absolute', top: 24, right: 24,
            color: 'rgba(255,255,255,0.35)',
            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            width: 44, height: 44,
          }}
        >
          <StopIcon />
        </IconButton>

        {/* Label */}
        {label && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textAlign: 'center' }}>
            {Icon && (
              <Box sx={{
                width: 48, height: 48, borderRadius: '14px',
                bgcolor: color + '22', border: `2px solid ${color}55`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon sx={{ fontSize: 26, color }} />
              </Box>
            )}
            <Typography sx={{
              fontSize: { xs: '1.1rem', md: '1.4rem' },
              fontWeight: 700,
              color: 'rgba(255,255,255,0.75)',
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              maxWidth: 500,
            }}>
              {label}
            </Typography>
          </Box>
        )}

        {/* Progress ring + tid */}
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width={320} height={320} style={{ transform: 'rotate(-90deg)' }}>
            {/* Track */}
            <circle cx={160} cy={160} r={R}
              fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={10} />
            {/* Progress */}
            <circle cx={160} cy={160} r={R}
              fill="none"
              stroke={ringColor}
              strokeWidth={10}
              strokeLinecap="round"
              strokeDasharray={`${dash} ${CIRC}`}
              style={{ transition: 'stroke-dasharray 0.9s linear, stroke 0.3s' }}
            />
          </svg>

          {/* Tidtext i mitten */}
          <Box sx={{
            position: 'absolute',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}>
            <Typography sx={{
              fontSize: { xs: '4.5rem', md: '6rem' },
              fontWeight: 900,
              color: done ? '#ef4444' : remaining <= 60 ? '#ef4444' : remaining <= 120 ? '#FF9800' : '#fff',
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
              fontFamily: "'DM Sans', monospace",
              letterSpacing: -2,
              transition: 'color 0.3s',
            }}>
              {formatTime(remaining)}
            </Typography>
            {done && (
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#ef4444', letterSpacing: 2, mt: 0.5 }}>
                TIDEN ÄR UTE
              </Typography>
            )}
          </Box>
        </Box>

        {/* Kontroller */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {!done && (
            <IconButton
              onClick={toggle}
              sx={{
                width: 64, height: 64,
                bgcolor: ringColor,
                color: '#fff',
                '&:hover': { bgcolor: ringColor, opacity: 0.85 },
                boxShadow: `0 0 32px ${ringColor}66`,
                borderRadius: '50%',
                transition: 'background-color 0.3s, box-shadow 0.3s',
              }}
            >
              {running
                ? <PauseIcon sx={{ fontSize: 32 }} />
                : <PlayArrowIcon sx={{ fontSize: 32 }} />
              }
            </IconButton>
          )}
          <IconButton
            onClick={stop}
            sx={{
              width: 48, height: 48,
              bgcolor: 'rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.5)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.14)', color: '#fff' },
              borderRadius: '50%',
              border: '1px solid rgba(255,255,255,0.12)',
            }}
          >
            <StopIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>
      </Box>
    </Dialog>
  );
}
